import type { ReactNode } from 'react';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { driver, type Config, type DriveStep, type Driver } from 'driver.js';
import { logWarn, logError } from '@/lib/logger';

const DEFAULT_DRIVER_CONFIG: Config = {
  showProgress: true,
  allowClose: true,
  allowKeyboardControl: true,
  smoothScroll: true,
  animate: true,
  stagePadding: 12,
  stageRadius: 16,
  // Overlay uses inline styles set by driver.js - these values match our CSS variables
  overlayOpacity: 0.55,
  overlayColor: 'rgba(15, 23, 42, 1)',
  popoverClass: 'cdb-driver-popover',
};

export type DriverTourDefinition = {
  id: string;
  steps: DriveStep[];
  config?: Partial<Config>;
  meta?: {
    title?: string;
    description?: string;
    tags?: string[];
  };
};

export type DriverStartOptions = {
  stepIndex?: number;
  steps?: DriveStep[];
  config?: Partial<Config>;
  ensureElements?: boolean;
};

type DriverContextValue = {
  registerTour: (definition: DriverTourDefinition) => () => void;
  unregisterTour: (id: string) => void;
  getTour: (id: string) => DriverTourDefinition | undefined;
  startTour: (id: string, options?: DriverStartOptions) => boolean;
  stopTour: () => void;
  isRunning: boolean;
  activeTourId: string | null;
  driver: Driver | null;
};

const DriverContext = createContext<DriverContextValue | undefined>(undefined);

type DriverProviderProps = {
  children: ReactNode;
  defaultConfig?: Partial<Config>;
};

const hasDocument = () => typeof document !== 'undefined';

const elementExists = (step: DriveStep): boolean => {
  if (!hasDocument()) return true;
  if (!step.element) return true;
  if (typeof step.element === 'string') {
    return !!document.querySelector(step.element);
  }
  if (typeof step.element === 'function') {
    try {
      return !!step.element();
    } catch {
      return false;
    }
  }
  return step.element instanceof Element;
};

export function DriverProvider({ children, defaultConfig }: DriverProviderProps) {
  const toursRef = useRef<Map<string, DriverTourDefinition>>(new Map());
  const driverRef = useRef<Driver | null>(null);
  const defaultConfigRef = useRef<Config>({
    ...DEFAULT_DRIVER_CONFIG,
    ...defaultConfig,
  });

  const [activeTourId, setActiveTourId] = useState<string | null>(null);
  const [isRunning, setIsRunning] = useState(false);
  const activeTourIdRef = useRef<string | null>(null);

  useEffect(() => {
    defaultConfigRef.current = {
      ...DEFAULT_DRIVER_CONFIG,
      ...defaultConfig,
    };

    // Laufende Tour mit neuen Defaults aktualisieren
    if (driverRef.current) {
      driverRef.current.setConfig(defaultConfigRef.current);
    }
  }, [defaultConfig]);

  useEffect(() => {
    return () => {
      driverRef.current?.destroy();
      driverRef.current = null;
    };
  }, []);

  const cleanup = useCallback(() => {
    setIsRunning(false);
    setActiveTourId(null);
    activeTourIdRef.current = null;
  }, []);

  const registerTour = useCallback((definition: DriverTourDefinition) => {
    if (!definition.id) {
      throw new Error('DriverTourDefinition benoetigt eine gueltige ID.');
    }

    const normalized: DriverTourDefinition = {
      ...definition,
      steps: definition.steps.map(step => ({ ...step })),
      config: definition.config ? { ...definition.config } : undefined,
    };

    if (toursRef.current.has(definition.id)) {
      logWarn(
        { tourId: definition.id },
        'Driver-Tour wird ueberschrieben, vorhandene Definition wird ersetzt.',
      );
    }

    toursRef.current.set(definition.id, normalized);

    return () => {
      toursRef.current.delete(definition.id);
      if (activeTourIdRef.current === definition.id) {
        driverRef.current?.destroy();
        driverRef.current = null;
        cleanup();
      }
    };
  }, [cleanup]);

  const unregisterTour = useCallback((tourId: string) => {
    toursRef.current.delete(tourId);
    if (activeTourIdRef.current === tourId) {
      driverRef.current?.destroy();
      driverRef.current = null;
      cleanup();
    }
  }, [cleanup]);

  const getTour = useCallback((tourId: string) => {
    return toursRef.current.get(tourId);
  }, []);

  const stopTour = useCallback(() => {
    if (driverRef.current?.isActive()) {
      driverRef.current.destroy();
    }
    driverRef.current = null;
    cleanup();
  }, [cleanup]);

  const startTour = useCallback((tourId: string, options?: DriverStartOptions) => {
    if (!hasDocument()) {
      return false;
    }

    const tour = toursRef.current.get(tourId);
    if (!tour) {
      logWarn({ tourId }, 'Driver-Tour konnte nicht gestartet werden: Definition nicht gefunden.');
      return false;
    }

    const baseSteps = options?.steps ?? tour.steps;
    if (!baseSteps.length) {
      logWarn({ tourId }, 'Driver-Tour ohne Schritte konfiguriert.');
      return false;
    }

    const ensureElements = options?.ensureElements ?? true;
    const preparedSteps = ensureElements
      ? baseSteps.filter((step, index) => {
        const exists = elementExists(step);
        if (!exists) {
          logWarn(
            { tourId, stepIndex: index },
            'Driver-Tour Schritt wird ausgelassen, da das Target-Element nicht gefunden wurde.',
          );
        }
        return exists;
      })
      : baseSteps;

    if (!preparedSteps.length) {
      logWarn(
        { tourId },
        'Driver-Tour konnte nicht gestartet werden: Keine gueltigen Schritte verfuegbar.',
      );
      return false;
    }

    // Beende eine laufende Tour, bevor eine neue gestartet wird
    if (driverRef.current?.isActive()) {
      driverRef.current.destroy();
    }

    const resolvedConfig: Config = {
      ...defaultConfigRef.current,
      ...tour.config,
      ...options?.config,
    };

    const userOnDestroyStarted = resolvedConfig.onDestroyStarted;
    const userOnDestroyed = resolvedConfig.onDestroyed;
    resolvedConfig.onDestroyStarted = (element, step, ctx) => {
      userOnDestroyStarted?.(element, step, ctx);
    };
    resolvedConfig.onDestroyed = (element, step, ctx) => {
      cleanup();
      driverRef.current = null;
      userOnDestroyed?.(element, step, ctx);
    };

    const userOnCloseClick = resolvedConfig.onCloseClick;
    resolvedConfig.onCloseClick = (element, step, ctx) => {
      userOnCloseClick?.(element, step, ctx);
      if (ctx.driver.isActive()) {
        ctx.driver.destroy();
      }
    };

    const userOnNextClick = resolvedConfig.onNextClick;
    resolvedConfig.onNextClick = (element, step, ctx) => {
      userOnNextClick?.(element, step, ctx);
      if (ctx.driver.hasNextStep()) {
        ctx.driver.moveNext();
      } else if (ctx.driver.isActive()) {
        ctx.driver.destroy();
      }
    };

    try {
      const instance = driver(resolvedConfig);
      instance.setSteps(preparedSteps);
      driverRef.current = instance;
      setActiveTourId(tourId);
      activeTourIdRef.current = tourId;
      setIsRunning(true);

      const startIndex = typeof options?.stepIndex === 'number' ? options.stepIndex : undefined;
      instance.drive(startIndex);
      return true;
    } catch (error) {
      driverRef.current = null;
      cleanup();
      logError(error as Error, { tourId, context: 'driver:startTour' });
      return false;
    }
  }, [cleanup]);

  const value = useMemo<DriverContextValue>(() => ({
    registerTour,
    unregisterTour,
    getTour,
    startTour,
    stopTour,
    isRunning,
    activeTourId,
    driver: driverRef.current,
  }), [
    registerTour,
    unregisterTour,
    getTour,
    startTour,
    stopTour,
    isRunning,
    activeTourId,
  ]);

  return (
    <DriverContext.Provider value={value}>
      {children}
    </DriverContext.Provider>
  );
}

export const useDriverContext = () => {
  const context = useContext(DriverContext);
  if (!context) {
    throw new Error('useDriverContext muss innerhalb eines DriverProvider verwendet werden.');
  }
  return context;
};
